import React, { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { isUndefined } from "lodash";

import Tooltip from "../../../../components/Tooltip";
import { UserModalProps } from "./types";
import Alert from "../../../../components/Alert";
import ButtonPrimary from "../../../../components/ButtonPrimary";
import Button from "../../../../components/Button";
import Modal from "../../../../components/Modal";
import { ModalHeader } from "../../../../components/Modal/ModalHeader";
import { ModalContent } from "../../../../components/Modal/ModalContent";
import { ModalFooter } from "../../../../components/Modal/ModalFooter";
import FormGroup from "../../../../components/FormGroup";
import Label from "../../../../components/Label";
import Input from "../../../../components/Input";
import Select from "../../../../components/Select";
import Textarea from "../../../../components/Textarea";
import Icon from "../../../../components/Icon";
import { IconName } from "../../../../components/Icon/types";
import { ICustomerPrincipal } from "../../../../services/api/user/types";
import { DEFAULT_ROLE, ROLES } from "./constants";
import { useAbility } from "../../../../hooks";
import { ACTION, SUBJECT } from "../../../../constants/permissions";
import { OWNER } from "../../../../services/api/user/constants";
import {
  getEmailLabel,
  getUsername,
  getUsernameLabel,
} from "../../../../utils/common";

import "./styles.scss";

export const UserModal: FC<UserModalProps> = ({
  isOpen,
  onDismiss,
  onSubmit,
  user,
  haveLicense,
  titleText,
  confirmButtonText,
  isSyncEmail,
  isUserNameDisabled,
  isShowMessage,
  isCanEdit = true,
  brand,
  isLoading,
  isUserCanResetPassword,
  onResetPassword,
  errors,
}) => {
  const { t, i18n } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [role, setRole] = useState("");
  const [license, setLicense] = useState("");
  const [message, setMessage] = useState("");
  const [isNameValid, setIsNameValid] = useState(true);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const ability = useAbility();
  const availableRoles = ROLES.filter((item) => {
    const { action, subject } = item.permissions;
    return ability.can(action, subject);
  });
  const emailLabel = useMemo(() => {
    return getEmailLabel(brand);
  }, [brand]);
  const usernameLabel = useMemo(() => {
    return getUsernameLabel(brand);
  }, [brand]);
  const canSetRole = ability.can(ACTION.WRITE, SUBJECT.ADMIN);

  const canEditRole = useMemo(() => {
    return user?.customer_role !== OWNER && canSetRole;
  }, [user, canSetRole]);
  const isRoleAvailable = useMemo(() => {
    return !user ? canSetRole : canEditRole;
  }, [user, canSetRole, canEditRole]);

  const roleValid = ability.can(ACTION.WRITE, SUBJECT.ADMIN)
    ? Boolean(role)
    : true;

  const isValid = useMemo(() => {
    return (
      Boolean(name) &&
      Boolean(email) &&
      Boolean(username) &&
      Boolean(license) &&
      roleValid
    );
  }, [name, email, username, license, roleValid]);

  useEffect(() => {
    if (user) {
      setFormData(user);
    }
  }, [user]);

  useEffect(() => {
    if (isUndefined(haveLicense)) {
      return;
    }

    setLicense(String(haveLicense));
  }, [haveLicense]);

  const setFormData = (userData: ICustomerPrincipal) => {
    setName(userData.name);
    setEmail(userData.email);
    setUsername(userData.username);
    setRole(userData.customer_role);
  };

  const onChangeName = (event: ChangeEvent<HTMLInputElement>) => {
    setIsNameValid(true);
    setName(event.target.value);
  };

  const onChangeEmail = (event: ChangeEvent<HTMLInputElement>) => {
    setIsEmailValid(true);
    setEmail(event.target.value);
  };

  const onChangeUsername = (event: ChangeEvent<HTMLInputElement>) => {
    setUsername(event.target.value);

    isSyncEmail && onChangeEmail(event);
  };

  const onChangeRole = (event: ChangeEvent<HTMLSelectElement>) => {
    setRole(event.target.value);
  };

  const onChangeLicense = (event: ChangeEvent<HTMLSelectElement>) => {
    setLicense(event.target.value);
  };

  const onChangeMessage = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.target.value);
  };

  const handleSubmit = () => {
    onSubmit(
      {
        name: name,
        email: email,
        username: getUsername(username, brand),
        /* eslint-disable-next-line */
        customer_role: role || DEFAULT_ROLE,
        message: message,
        // eslint-disable-next-line @typescript-eslint/camelcase
        communication_locale: i18n.language,
      },
      license === "true",
    );
  };

  useEffect(() => {
    if (errors) {
      if (errors.name) {
        setName("");
        setIsNameValid(false);
      }

      if (errors.email) {
        setEmail("");
        setUsername("");
        setIsEmailValid(false);
      }
    }
  }, [errors]);

  const errorList = errors ? Object.values(errors) : [];

  return (
    <Modal className="ams-user-modal" isOpen={isOpen}>
      <ModalHeader className="ams-user-modal__header">
        <span>{titleText}</span>

        {isUserCanResetPassword && (
          <button
            className="ams-reset-password__link-button"
            onClick={onResetPassword}
          >
            {t("resetUserPassword")}
          </button>
        )}
      </ModalHeader>
      {errorList?.length ? (
        <div className="ams-user-modal__validation-alert">
          <Alert
            className="ams-user-modal__error-alert"
            variant="error"
            content={
              <ul className="ams-user-modal__error-list">
                {errorList.map((error) => {
                  return (
                    <li className="ams-user-modal__error-list-item" key={error}>
                      {error}
                    </li>
                  );
                })}
              </ul>
            }
          />
        </div>
      ) : null}

      <ModalContent className="ams-user-modal__content">
        <FormGroup>
          <Label>{t("name")}</Label>
          <Input
            autoFocus
            required
            disabled={!isCanEdit}
            id="user-name-input"
            value={name}
            error={!isNameValid}
            onChange={onChangeName}
          />
        </FormGroup>

        <FormGroup>
          <Label>{t(usernameLabel)}</Label>
          <Input
            disabled={isUserNameDisabled || !isCanEdit}
            value={username}
            error={!isEmailValid}
            onChange={onChangeUsername}
            id="username-input"
          />
        </FormGroup>

        {!isSyncEmail ? (
          <FormGroup>
            <Label>{t(emailLabel)}</Label>
            <Input
              required
              type="email"
              id="user-email-input"
              disabled={!isCanEdit}
              value={email}
              error={!isEmailValid}
              onChange={onChangeEmail}
            />
          </FormGroup>
        ) : null}

        {isRoleAvailable ? (
          <FormGroup>
            <Label className="ams-label__role">
              {t("role")}
              <Tooltip
                showOnClick
                placement="right"
                skidding={30}
                activator={<Icon>{"help" as IconName}</Icon>}
              >
                {ROLES.map((item) => {
                  return (
                    <div key={item.value}>{t(`roleTooltip.${item.value}`)}</div>
                  );
                })}
              </Tooltip>
            </Label>
            <Select
              required
              id="user-role-select"
              value={role}
              disabled={!isCanEdit}
              onChange={onChangeRole}
            >
              <option value="">{t("chooseRole")}</option>
              {availableRoles.map((item) => {
                return (
                  <option key={item.value} value={item.value}>
                    {t(item.value.toLowerCase())}
                  </option>
                );
              })}
            </Select>
          </FormGroup>
        ) : null}

        <FormGroup>
          <Label>{t("productLicense")}</Label>
          <Select
            required
            id="user-license-select"
            value={license}
            onChange={onChangeLicense}
          >
            <option value="">---</option>
            <option value="true">{t("yes")}</option>
            <option value="false">{t("no")}</option>
          </Select>
        </FormGroup>

        {isShowMessage ? (
          <FormGroup>
            <Label>{t("optionalMessage")}</Label>
            <Textarea
              id="user-message"
              value={message}
              onChange={onChangeMessage}
            />
          </FormGroup>
        ) : null}
      </ModalContent>

      <ModalFooter>
        <Button onClick={onDismiss}>{t("cancel")}</Button>
        <ButtonPrimary
          className="ams-modal__action"
          onClick={handleSubmit}
          disabled={!isValid}
          isLoading={isLoading}
        >
          {confirmButtonText}
        </ButtonPrimary>
      </ModalFooter>
    </Modal>
  );
};
